import React, { useState, useEffect } from 'react';
import { 
  ExternalLink, 
  Check, 
  Link2,
  ChevronRight,
  Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';

const Apps = () => {
  const { token } = useAuth();
  const [apps, setApps] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApps(response.data.apps);
      setByCategory(response.data.by_category);
    } catch (error) {
      console.error('Error fetching apps:', error);
    }
    setLoading(false);
  };

  const toggleConnection = async (appId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/apps`,
        { app_id: appId, connected: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApps();
      toast.success(currentStatus ? 'Disconnected' : 'Connected');
    } catch (error) {
      toast.error('Could not update connection');
    }
  };

  const categories = ['all', ...Object.keys(byCategory)];

  const filteredApps = apps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          app.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const connectedCount = apps.filter(a => a.connected).length;

  const categoryIcons = {
    'Finance': '📊',
    'Banking': '🏦',
    'Legal': '⚖️',
    'Productivity': '📝',
    'Marketing': '🎯'
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl headline text-black mb-2">Business Apps</h1>
        <p className="text-black/60 body-text">
          Connect your essential business tools.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Connected</p>
          <p className="text-2xl headline text-black mt-1">{connectedCount}</p>
          <p className="text-sm text-black/60 mt-1">of {apps.length} apps</p>
        </div>
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Categories</p>
          <p className="text-2xl headline text-black mt-1">{Object.keys(byCategory).length}</p>
          <p className="text-sm text-black/60 mt-1">app categories</p>
        </div>
        <div className="bg-white rounded-none p-5 border border-border col-span-2 sm:col-span-1">
          <p className="text-xs text-black/40 uppercase tracking-wide">Setup Progress</p>
          <div className="mt-2">
            <div className="h-2 bg-secondary rounded-none">
              <div 
                className="h-full bg-primary rounded-none transition-all"
                style={{ width: `${(connectedCount / apps.length) * 100}%` }}
              />
            </div>
          </div>
          <p className="text-sm text-black/60 mt-2">{Math.round((connectedCount / apps.length) * 100)}% complete</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search apps..."
            className="pl-10 bg-white border-border rounded-none"
            data-testid="search-apps"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-none text-sm transition-all whitespace-nowrap ${
                selectedCategory === cat
                  ? 'bg-primary text-white subhead'
                  : 'bg-secondary text-black/60 hover:bg-secondary/80'
              }`}
            >
              {cat === 'all' ? 'All Apps' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Apps Grid */}
      {loading ? (
        <div className="text-center py-8 text-black/40">Loading...</div>
      ) : selectedCategory === 'all' ? (
        // Grouped by category
        <div className="space-y-6">
          {Object.entries(byCategory).map(([category, categoryApps]) => {
            const filteredCategoryApps = categoryApps.filter(app =>
              app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              app.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
            
            if (filteredCategoryApps.length === 0) return null;

            return (
              <div key={category} className="bg-white rounded-none border border-border overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                  <span className="text-xl">{categoryIcons[category] || '📱'}</span>
                  <h2 className="subhead text-black">{category}</h2>
                  <span className="text-sm text-black/40 ml-auto">
                    {filteredCategoryApps.filter(a => a.connected).length}/{filteredCategoryApps.length} connected
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {filteredCategoryApps.map((app) => (
                    <AppRow 
                      key={app.id} 
                      app={app} 
                      onToggle={() => toggleConnection(app.id, app.connected)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Flat list for selected category
        <div className="bg-white rounded-none border border-border overflow-hidden">
          {filteredApps.length > 0 ? (
            <div className="divide-y divide-border">
              {filteredApps.map((app) => (
                <AppRow 
                  key={app.id} 
                  app={app} 
                  onToggle={() => toggleConnection(app.id, app.connected)}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-black/40">No apps found.</p>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <div className="bg-primary/10 rounded-none p-6 border border-primary/20">
        <h3 className="subhead text-black mb-2">Need help connecting?</h3>
        <p className="text-sm text-black/60 mb-4">
          Brian can help you set up any of these tools. Just ask in the chat!
        </p>
        <p className="text-xs text-black/40">
          Tip: Start with Xero for accounting and Google Workspace for email.
        </p>
      </div>
    </div>
  );
};

const AppRow = ({ app, onToggle }) => {
  return (
    <div className="flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors">
      <div className="w-12 h-12 rounded-none bg-secondary flex items-center justify-center text-2xl">
        {app.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="subhead text-black">{app.name}</p>
        <p className="text-sm text-black/60">{app.description}</p>
      </div>
      <div className="flex items-center gap-3">
        {app.connected ? (
          <>
            <span className="text-xs text-[#1E491A] flex items-center gap-1">
              <Check className="h-3 w-3" />
              Connected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="text-black/40 hover:text-black"
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onToggle}
            className="rounded-none border-border hover:border-primary"
            data-testid={`connect-${app.id}`}
          >
            <Link2 className="h-4 w-4 mr-2" />
            Connect
          </Button>
        )}
        <a
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-black/40 hover:text-primary rounded-none"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
};

export default Apps;
